import { createTheme } from '@mui/material/styles';
import { ink } from '../defaults/designSystem';

export const theme = createTheme({
  palette: {
    brand: {
      yellow: '#FFDC00'
    },
    primary: {
      main: '#1B9A9D',
      dark: '#086C6F',
      light: '#E4F3F3',
      contrast: '#FFFFFF'
    },
    secondary: {
      main: '#2B2B2B',
      dark: '#101010',
      light: '#E6E6E6',
      contrast: '#FFFFFF'
    },
    button: {
      main: '#FFDC00',
      dark: '#FFDC00',
      light: '#FFFBE0',
      contrast: '#2B2B2B'
    },
    error: {
      main: '#A81515',
      dark: '#8D1111',
      light: '#FCE4E4',
      contrast: '#FFFFFF'
    },
    warning: {
      main: '#ED6C02',
      dark: '#B36200',
      light: '#FFE8CC',
      contrast: '#FFFFFF'
    },
    notification: {
      main: '#FF4A4A',
      contrast: '#FFFFFF'
    },
    info: {
      main: '#0074AD',
      dark: '#00334D',
      light: '#E5F1F7',
      contrast: '#FFFFFF'
    },
    success: {
      main: '#00960F',
      dark: '#115F19',
      light: '#E5F4E7',
      contrast: '#FFFFFF'
    },
    default: {
      contrast: '#2B2B2B'
    },
    action: {
      divider: 'rgba(0, 0, 0, 0.23)'
    },
    table: {
      header: '#F5F5F5'
    },
    accents: {
      gray: '#F7F8F9'
    },
    chip: {
      status: {
        filled: {
          main: '#00334D',
          background: '#E5F1F7'
        },
        in_progress: {
          main: '#086C6F',
          background: '#E4F3F3'
        },
        approved: {
          main: '#115F19',
          background: '#E5F4E7'
        },
        completed: {
          main: '#2B2B2B',
          background: 'rgba(0, 0, 0, 0.04)'
        },
        cancelled: {
          main: '#8D1111',
          background: '#FCE4E4'
        },
        no_show: {
          main: '#DA1B1B',
          background: '#FCE4E4'
        },
        unfilled: {
          main: '#A7560C',
          background: '#FEF3E5'
        }
      }
    },
    typography: {
      main: '#2B2B2B',
      info: '#727272',
      disabled: '#999999'
    },
    alert: {
      info: {
        fill: '#E6F3FA',
        dark: '#00334D',
        content: '#013654'
      },
      success: {
        fill: '#EAF2EA',
        dark: '#115F19',
        content: '#123214'
      },
      error: {
        fill: '#FBEAEA',
        content: '#541313',
        icon: '#DA1B1B'
      },
      warning: {
        fill: '#FFF4E5',
        content: '#663C00',
        icon: '#FF8C04'
      }
    },
    container: {
      border: 'rgba(0, 0, 0, 0.12)'
    },
    common: {
      white: '#FFFFFF'
    },
    icons: {
      gray: '#BDBDBD'
    },
    modals: {
      border: 'rgba(0, 0, 0, 0.5)'
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 992,
      lg: 1440,
      xl: 1920,
    },
  },
  // mui uses rems by default -- override to use px instead
  typography: {
    h1: {
      fontSize: '96px',
      fontWeight: 600
    },
    h2: {
      fontSize: '60px',
      fontWeight: 600
    },
    h3: {
      fontSize: '48px',
      fontWeight: 700
    },
    h4: {
      fontSize: '34px',
      fontWeight: 700
    },
    h5: {
      fontSize: '24px',
      fontWeight: 400,
    },
    h6: {
      fontSize: '18px',
      fontWeight: 700
    },
    body1: {
      fontSize: '16px'
    },
    body2: {
      fontSize: '14px'
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 700
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 700
    },
    overline: {
      fontSize: '12px',
      fontWeight: 700,
      textTransform: 'uppercase'
    },
    caption: {
      fontSize: '12px'
    }
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '16px'
        },
        asterisk: {
          color: '#DA1B1B'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        sizeSmall: {
          fontSize: '14px'
        },
        sizeMedium: {
          fontSize: '16px'
        },
        sizeLarge: {
          fontSize: '17px'
        },
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          backgroundColor: ink[400]
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        filledSuccess: {
          backgroundColor: '#E5F4E7',
          border: '1px solid #00960F',
          color: '#123214',
        },
        filledError: {
          backgroundColor: '#FCE4E4',
          border: '1px solid #DA1B1B',
          color: '#541313',
        },
        filledWarning: {
          backgroundColor: '#FDF0E6',
          border: '1px solid #FF8C04',
          color: '#5F2B01',
        },
        filledInfo: {
          backgroundColor: '#E6F3FA',
          border: '1px solid #0074AD',
          color: '#013654',
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#727272',
          fontSize: '14px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        deleteIcon: {
          color: '#2B2B2B'
        },
        gray: {
          backgroundColor: '#F4F4F4',
          border: 'none',
          color: '#999999'
        },
        info: {
          backgroundColor: '#E5F1F7',
          boder: '#00334D',
          color: '#00334D'
        },
        root: {
          border: '1px solid #086C6F',
          backgroundColor: '#E4F3F3',
          color: '#086C6F',
          fontWeight: '700'
        },
        outlinedDefault: {
          border: '1px solid #2B2B2B',
          backgroundColor: '#FFFFFF',
          color: '#2B2B2B'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '3px 9px'
        }
      }
    }
  }
});
